import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "明星动态", to: "/rexun?cat=明星动态" },
  { label: "影视综艺", to: "/rexun?cat=影视综艺" },
  { label: "网络热梗", to: "/rexun?cat=网络热梗" },
  { label: "热搜解读", to: "/rexun?cat=热搜解读" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    const target = new URL(to, "https://x.example");
    if (target.pathname === "/rexun") {
      return (
        location.pathname === "/rexun" &&
        (target.searchParams.get("cat") || "全部瓜田") ===
          (new URLSearchParams(location.search).get("cat") || "全部瓜田")
      );
    }
    return location.pathname === target.pathname;
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/rexun?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur hairline border-b" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 items-center justify-between gap-4 px-4 md:px-6">
        {/* brand */}
        <Link to="/" className="group flex items-center gap-2 whitespace-nowrap" aria-label="瓜田日报 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-flower-fill text-lg" aria-hidden="true"></i>
          </span>
          <span className="font-display text-xl font-bold tracking-wide text-foreground-950 group-hover:text-primary-700">
            瓜田日报
          </span>
        </Link>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className={`relative whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(l.to)
                  ? "font-semibold text-primary-700"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              {l.label}
              {isActive(l.to) && (
                <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-primary-500" />
              )}
            </Link>
          ))}
        </nav>

        {/* right actions */}
        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center lg:flex" role="search">
            <div className="flex items-center rounded-full border border-background-300 bg-background-50 px-3 py-1.5">
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜瓜田新鲜事"
                aria-label="站内搜索"
                className="w-36 bg-transparent pl-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>

          <Link
            to="/about#contact"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            <i className="ri-edit-line" aria-hidden="true"></i>
            投稿吃瓜
          </Link>

          {/* hamburger */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "关闭菜单" : "打开菜单"}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 md:hidden"
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* mobile panel */}
      {open && (
        <div className="border-t hairline bg-background-50 px-4 pb-4 md:hidden">
          <form onSubmit={submitSearch} className="pt-3" role="search">
            <div className="flex items-center rounded-full border border-background-300 bg-background-50 px-3 py-2">
              <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜瓜田新鲜事"
                aria-label="站内搜索"
                className="w-full bg-transparent pl-2 text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <nav className="mt-3 grid grid-cols-2 gap-2" aria-label="移动端导航">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className={`rounded-md px-3 py-2.5 text-sm ${
                  isActive(l.to)
                    ? "bg-primary-100 font-semibold text-primary-700"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}